/**
 * 📱 Install PWA Button — floating chip that only appears when the
 * browser has told us the app is installable (via `beforeinstallprompt`).
 *
 * Handles:
 *   • Android Chrome / Edge / Samsung Internet — native install prompt.
 *   • Desktop Chrome / Edge — same native prompt.
 *   • iOS Safari — no `beforeinstallprompt`; instead, shows a one-time
 *     "Add to Home Screen" cheatsheet if the user isn't already
 *     running in standalone mode.
 *
 * The button self-dismisses after a successful install AND remembers
 * a "user closed it" flag so it doesn't nag every session.
 */
import { useEffect, useState } from "react";

const DISMISS_KEY = "omni-pwa-install-dismissed-v2";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function InstallPWAButton() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) { setDismissed(true); return; }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      setDeferred(null);
      localStorage.setItem(DISMISS_KEY, "installed");
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt. Show a hint instead.
    if (isIOS() && !isStandalone()) {
      const t = setTimeout(() => setShowIosHint(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onPrompt);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (dismissed) return null;

  const closeAndRemember = () => {
    localStorage.setItem(DISMISS_KEY, "user");
    setDismissed(true);
  };

  if (deferred) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-amber-500 to-yellow-600 text-black shadow-lg shadow-amber-500/30 border border-amber-200/50"
        data-testid="pwa-install-chip"
      >
        <button
          onClick={async () => {
            deferred.prompt();
            try {
              await deferred.userChoice;
            } finally {
              setDeferred(null);
              localStorage.setItem(DISMISS_KEY, "prompted");
            }
          }}
          className="text-xs font-black tracking-wide"
          data-testid="pwa-install-btn"
        >
          📱 Install OmniMuscle App
        </button>
        <button
          onClick={closeAndRemember}
          aria-label="Dismiss install prompt"
          className="text-black/60 hover:text-black text-lg leading-none px-1"
          data-testid="pwa-install-dismiss"
        >×</button>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] max-w-xs px-3 py-2 rounded-xl bg-black/95 border border-amber-500/40 text-neutral-100 shadow-lg text-xs"
        data-testid="pwa-ios-hint"
      >
        <div className="flex items-start gap-2">
          <div className="flex-1">
            📱 <b className="text-amber-300">Install on iPhone:</b> tap Safari&apos;s
            share button, then <b>Add to Home Screen</b>.
          </div>
          <button
            onClick={closeAndRemember}
            aria-label="Dismiss"
            className="text-neutral-500 hover:text-neutral-200 text-lg leading-none px-1"
            data-testid="pwa-ios-dismiss"
          >×</button>
        </div>
      </div>
    );
  }

  return null;
}
